<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import DataTable from 'primevue/datatable'
import type { DataTableSortEvent } from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import Paginator from 'primevue/paginator'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'
import ErrorState from '@/components/common/ErrorState.vue'
import EmptyState from '@/components/common/EmptyState.vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'
import AssetFilterBar from '@/components/Assets/AssetFilterBar.vue'
import AssetDetailDialog from '@/components/Assets/AssetDetailDialog.vue'

import { assetService } from '@/services/assetService'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import { notificationService } from '@/services/notificationService'
import { exportAssetsToCsv, exportAssetsToExcel, exportAssetsToPdf } from '@/utils/assetExport'
import {
  getWarrantyStatusBadgeClass,
  getInstallationStatusBadgeClass,
  getInstallationStatusLabel,
  formatWarrantyDuration,
  formatRemainingDays,
  formatAssetDate
} from '@/utils/assetUtils'
import type { AssetFilterOptions, AssetQueryParams, InstalledAsset } from '@/types/asset'
import type { AxiosError } from 'axios'
import { EyeIcon, GlobeIcon, DownloadIcon, FileExcleIcon, FilePdfIcon, RefreshIcon } from '@/icons'

// ============ STATE ============
const assets = ref<InstalledAsset[]>([])
const pagination = ref({ total: 0, page: 1, limit: 25, totalPages: 1 })
const filterOptions = ref<AssetFilterOptions>({
  customers: [],
  organizations: [],
  engineers: [],
  installationStatuses: ['installed', 'not-installed', 'unknown']
})

const filters = ref<AssetQueryParams>({
  page: 1,
  limit: 25,
  sortBy: 'installationDate',
  sortOrder: 'DESC'
})

const loading = ref(false)
const loadingFilterOptions = ref(false)
const error = ref<string | null>(null)

const exportingCsv = ref(false)
const exportingExcel = ref(false)
const exportingPdf = ref(false)

const first = ref(0)
const rows = ref(25)

const selectedAssetId = ref<string | null>(null)
const showDetailDialog = ref(false)

// ============ DATA FETCHING ============
const fetchAssets = async () => {
  loading.value = true
  error.value = null
  try {
    const result = await assetService.getAssets(filters.value)
    assets.value = result.data
    pagination.value = result.pagination
  } catch (err) {
    error.value = getAxiosErrorMessage(err as AxiosError)
  } finally {
    loading.value = false
  }
}

const fetchFilterOptions = async () => {
  loadingFilterOptions.value = true
  try {
    filterOptions.value = await assetService.getFilterOptions()
  } catch {
    // Non-fatal — dropdowns just stay empty if this fails
  } finally {
    loadingFilterOptions.value = false
  }
}

const refreshAll = async () => {
  await Promise.all([fetchAssets(), fetchFilterOptions()])
}

const hasActiveFilters = computed(() => {
  const f = filters.value
  return !!(
    f.search ||
    f.customerName ||
    f.organization ||
    f.engineer ||
    f.warrantyStatus ||
    f.installationStatus ||
    f.dateFrom ||
    f.dateTo
  )
})

const clearFilters = () => {
  filters.value = {
    page: 1,
    limit: filters.value.limit,
    sortBy: filters.value.sortBy,
    sortOrder: filters.value.sortOrder
  }
}

// Debounce so typing in the search box doesn't fire a request per keystroke.
let debounceHandle: ReturnType<typeof setTimeout> | null = null
watch(
  filters,
  () => {
    if (debounceHandle) clearTimeout(debounceHandle)
    debounceHandle = setTimeout(fetchAssets, 300)
  },
  { deep: true }
)

onBeforeUnmount(() => {
  if (debounceHandle) clearTimeout(debounceHandle)
})

onMounted(() => {
  fetchAssets()
  fetchFilterOptions()
})

// ============ PAGINATION ============
const onPageChange = (event: { first: number; rows: number }) => {
  first.value = event.first
  rows.value = event.rows
  filters.value = {
    ...filters.value,
    page: Math.floor(event.first / event.rows) + 1,
    limit: event.rows
  }
}

// ============ SORTING ============
const onSort = (event: DataTableSortEvent) => {
  if (!event.sortField || typeof event.sortField !== 'string') return
  filters.value = {
    ...filters.value,
    sortBy: event.sortField as AssetQueryParams['sortBy'],
    sortOrder: event.sortOrder === -1 ? 'DESC' : 'ASC',
    page: 1
  }
  first.value = 0
}

// ============ ROW DETAIL ============
const openAssetDetail = (asset: InstalledAsset) => {
  selectedAssetId.value = asset.assetId
  showDetailDialog.value = true
}

// ============ EXPORT (respects current filters, ignores pagination) ============
const currentExportParams = () => {
  const params = { ...filters.value }
  delete params.page
  delete params.limit
  return params
}

const handleExportCsv = async () => {
  exportingCsv.value = true
  try {
    const data = await assetService.exportAssets(currentExportParams())
    exportAssetsToCsv(data)
    notificationService.success(`Exported ${data.length} asset(s) to CSV`)
  } catch (err) {
    notificationService.error(getAxiosErrorMessage(err as AxiosError))
  } finally {
    exportingCsv.value = false
  }
}

const handleExportExcel = async () => {
  exportingExcel.value = true
  try {
    const data = await assetService.exportAssets(currentExportParams())
    exportAssetsToExcel(data)
    notificationService.success(`Exported ${data.length} asset(s) to Excel`)
  } catch (err) {
    notificationService.error(getAxiosErrorMessage(err as AxiosError))
  } finally {
    exportingExcel.value = false
  }
}

const handleExportPdf = async () => {
  exportingPdf.value = true
  try {
    const data = await assetService.exportAssets(currentExportParams())
    await exportAssetsToPdf(data)
    notificationService.success(`Exported ${data.length} asset(s) to PDF`)
  } catch (err) {
    notificationService.error(getAxiosErrorMessage(err as AxiosError))
  } finally {
    exportingPdf.value = false
  }
}

// Tooltip for long remarks
const tooltipVisible = ref(false)
const tooltipText = ref('')
const tooltipTarget = ref<HTMLElement>()

const showTooltip = (event: MouseEvent, text: string) => {
  if (!text) return
  tooltipTarget.value = event.currentTarget as HTMLElement
  tooltipText.value = text
  tooltipVisible.value = true
}
const hideTooltip = () => {
  tooltipVisible.value = false
  tooltipTarget.value = undefined
  tooltipText.value = ''
}
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Installed Assets" />

    <CardComponent
      className="max-w-full mb-4"
      footerClass="justify-end"
      title="Asset Filters"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
      bodyClass="flex flex-col"
    >
      <template #header>
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div class="flex flex-wrap gap-2">
            <BaseButton
              text="CSV"
              size="xss"
              variant="secondary"
              :icon="DownloadIcon"
              :loading="exportingCsv"
              tooltip="Export current view to CSV"
              @click="handleExportCsv"
            />
            <BaseButton
              text="Excel"
              size="xss"
              variant="secondary"
              :icon="FileExcleIcon"
              :loading="exportingExcel"
              tooltip="Export current view to Excel"
              @click="handleExportExcel"
            />
            <BaseButton
              text="PDF"
              size="xss"
              variant="secondary"
              :icon="FilePdfIcon"
              :loading="exportingPdf"
              tooltip="Export current view to PDF"
              @click="handleExportPdf"
            />
          </div>

          <div class="flex flex-wrap gap-2">
            <BaseButton
              v-if="hasActiveFilters"
              text="Clear Filters"
              size="xss"
              variant="ghost"
              @click="clearFilters"
            />
            <BaseButton
              size="xss"
              :icon="RefreshIcon"
              :loading="loading || loadingFilterOptions"
              tooltip="Refresh"
              @click="refreshAll"
            />
          </div>
        </div>
      </template>

      <template #body>
        <AssetFilterBar
          v-model="filters"
          :filter-options="filterOptions"
        />
      </template>

    </CardComponent>

    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Asset Inventory"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
      bodyClass="grid grid-cols-1"
    >
      <template #body>
        <LoaderDefault v-if="loading" title="Loading installed assets..." />

        <div v-else class="flex flex-col">
          <ErrorState v-if="error" :message="error" :show-test-login="true" @retry="fetchAssets" />

          <EmptyState
            v-else-if="assets.length === 0"
            :icon="GlobeIcon"
            title="No installed assets found"
            description="No installed items match your current search/filters."
            hint="Assets are generated automatically from Tender Reports as they're created"
          />

          <DataTable
            v-else
            :value="assets"
            class="w-full"
            :lazy="true"
            :sortField="filters.sortBy"
            :sortOrder="filters.sortOrder === 'DESC' ? -1 : 1"
            scrollable
            scrollHeight="500px"
            resizableColumns
            columnResizeMode="fit"
            showGridlines
            @sort="onSort"
            @row-click="(e) => openAssetDetail(e.data)"
            :pt="{
              table: { style: { borderCollapse: 'separate', borderSpacing: '0' } },
              thead: { style: { position: 'sticky', top: '0', zIndex: '20' } },
              tbody: { style: { position: 'relative', zIndex: '10' } },
              bodyRow: { style: { cursor: 'pointer' } }
            }"
          >
            <Column sortable frozen field="customerName" header="Customer Name"
              headerStyle="position: sticky; left: 0; z-index: 30;"
              bodyStyle="position: sticky; left: 0; z-index: 25;"
            >
              <template #body="{ data }">
                <span class="text-xs font-medium">{{ data.customerName || 'N/A' }}</span>
              </template>
            </Column>

            <Column field="organization" header="Organization">
              <template #body="{ data }"><span class="text-xs">{{ data.organization || 'N/A' }}</span></template>
            </Column>

            <Column field="referenceNumber" header="Tender Reference No">
              <template #body="{ data }"><span class="text-xs break-all">{{ data.referenceNumber || 'N/A' }}</span></template>
            </Column>

            <Column field="t247Id" header="Tender ID">
              <template #body="{ data }"><span class="text-xs">{{ data.t247Id || 'N/A' }}</span></template>
            </Column>

            <Column sortable field="itemName" header="Item Name">
              <template #body="{ data }"><span class="text-xs">{{ data.itemName || 'N/A' }}</span></template>
            </Column>

            <Column sortable field="serialNumber" header="Serial Number">
              <template #body="{ data }"><span class="text-xs">{{ data.serialNumber || 'N/A' }}</span></template>
            </Column>

            <Column field="warranty" header="Warranty">
              <template #body="{ data }">
                <span class="text-xs">{{ formatWarrantyDuration(data.warrantyYears, data.warrantyMonths) }}</span>
              </template>
            </Column>

            <Column sortable field="warrantyExpiry" header="Warranty Expiry">
              <template #body="{ data }">
                <div class="flex items-center gap-1.5 text-xs">
                  <span class="w-2 h-2 rounded-full flex-shrink-0" :class="getWarrantyStatusBadgeClass(data.warrantyStatus).includes('red') ? 'bg-red-500' : ''"></span>
                  {{ formatAssetDate(data.warrantyExpiry) }}
                </div>
              </template>
            </Column>

            <Column field="warrantyRemaining" header="Warranty Remaining">
              <template #body="{ data }">
                <Badge :value="formatRemainingDays(data.remainingDays)" :class="getWarrantyStatusBadgeClass(data.warrantyStatus)" />
              </template>
            </Column>

            <Column field="installationStatus" header="Installation Status">
              <template #body="{ data }">
                <Badge :value="getInstallationStatusLabel(data.installationStatus)" :class="getInstallationStatusBadgeClass(data.installationStatus)" />
              </template>
            </Column>

            <Column field="engineer" header="Engineer">
              <template #body="{ data }"><span class="text-xs">{{ data.engineer || 'Not Assigned' }}</span></template>
            </Column>

            <Column field="consignee" header="Consignee">
              <template #body="{ data }"><span class="text-xs">{{ data.consignee || 'N/A' }}</span></template>
            </Column>

            <Column sortable field="installationDate" header="Installation Date">
              <template #body="{ data }"><span class="text-xs">{{ formatAssetDate(data.installationDate) }}</span></template>
            </Column>

            <Column field="remarks" header="Remarks">
              <template #body="{ data }">
                <div
                  class="line-clamp-2 text-xs cursor-help max-w-[160px]"
                  @mouseenter="(event) => showTooltip(event, data.remarks)"
                  @mouseleave="hideTooltip"
                >
                  {{ data.remarks || '-' }}
                </div>
              </template>
            </Column>

            <Column field="reportDate" header="Report Created Date">
              <template #body="{ data }"><span class="text-xs">{{ formatAssetDate(data.reportDate) }}</span></template>
            </Column>

            <Column
              frozen
              field="action"
              header="Actions"
              headerStyle="width: 60px; position: sticky; right: 0; z-index: 30;"
              bodyStyle="position: sticky; right: 0; z-index: 25;"
            >
              <template #body="{ data }">
                <BaseButton
                  size="xss"
                  variant="secondary"
                  :icon="EyeIcon"
                  :tooltip="`View ${data.itemName} details`"
                  tooltipPosition="bottom"
                  @click="openAssetDetail(data)"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </template>

      <template #footer v-if="!loading && !error && assets.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ first + 1 }}-{{ Math.min(first + rows, pagination.total) }} of {{ pagination.total }} installed assets
          </div>
          <Paginator
            v-model:first="first"
            v-model:rows="rows"
            :rowsPerPageOptions="[10, 25, 50, 100]"
            :totalRecords="pagination.total"
            @page="onPageChange"
          />
        </div>
      </template>
    </CardComponent>

  </AdminLayout>

  <AssetDetailDialog v-model="showDetailDialog" :asset-id="selectedAssetId" />

  <TooltipPortal
    :visible="tooltipVisible"
    :text="tooltipText"
    :target="tooltipTarget"
    position="auto"
    :max-width="'400px'"
    :multiline="true"
  />
</template>
